import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";
import { usePartiallyUpdateTouchpoint } from "hooks/api/use-touchpoints";
import { Switch } from "ui/switch";
import { Box } from "ui/box";
import { TextField, TextFieldWithCounter } from "ui/text-field";
import { TextArea } from "ui/text-area";
import { Select } from "ui/select";
import { IconButton } from "ui/icon-button";
import { FormDialog } from "ui/form-dialog";
import { Typography } from "ui/typography";
import toast from "components/partials/toast/toast";
import { COPY_CODE_MAX_LENGTH } from "components/modals/creative-modal/creative-modal-consts";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { getErrorMessage, getFYOptions } from "utilities";
import Icons from "assets";

const touchpointVersionApi = getTouchpointVersion();

/** "PARENT | NAME" under the headline: 14px bold caps on a wide track. */
const VERSION_EYEBROW_SX: SxProps<Theme> = (theme) => ({
  ...theme.typography.body2,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: theme.palette.grey[500],
});

const FISCAL_YEAR_ROW_SX: SxProps<Theme> = { display: "flex", gap: 1 };

export interface SubmitFormProps {
  id?: string;
  description: string;
  applyClientTheme?: boolean;
  fiscalYear: number;
  copyCode?: string;
}

interface FormData {
  description: string;
  fiscalYear: number;
  copyCode?: string;
}

export enum FormType {
  NEW = "new",
  EDIT = "edit",
  NEW_VERSION = "newVersion",
}

interface VersionEditModalProps {
  parentName?: string;
  applyClientTheme?: boolean;
  formType: FormType;
  helperText?: string;
  id?: string;
  isOpen: boolean;
  name?: string;
  onClose: () => void;
  onSubmit?: (props: SubmitFormProps) => Promise<void> | void;
  onCancel?: () => void;
  placeholderNotes?: string;
  saveText?: string;
  isEmailType?: boolean;
  touchpointId?: string;
  fiscalYear?: number;
  copyCode?: string;
  isTemplate?: boolean;
}

const VersionEditModal = ({
  isOpen,
  onClose,
  formType,
  name,
  onSubmit,
  onCancel,
  placeholderNotes = "",
  helperText,
  saveText,
  parentName,
  id,
  applyClientTheme = true,
  isEmailType = false,
  touchpointId,
  fiscalYear: initialFiscalYear,
  copyCode: initialCopyCode,
  isTemplate = false,
}: VersionEditModalProps) => {
  const currentCampaign = useCurrentCampaign();
  const currentClient = useCurrentClient();
  const partialUpdateMutation = usePartiallyUpdateTouchpoint();
  const [isClientThemeApplied, setIsClientThemeApplied] = useState<boolean>(applyClientTheme);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fyOptions = getFYOptions();
  const canEditCopyCode = !initialCopyCode;

  const methods = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      description: placeholderNotes,
      fiscalYear: initialFiscalYear,
      copyCode: initialCopyCode || "",
    },
  });

  const {
    control,
    reset,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    if (isOpen) {
      reset({
        description: placeholderNotes || "",
        fiscalYear: initialFiscalYear,
        copyCode: initialCopyCode || "",
      });
      setIsClientThemeApplied(applyClientTheme);
    }
  }, [isOpen, placeholderNotes, initialFiscalYear, initialCopyCode, applyClientTheme, reset]);

  const isEditFormType = formType === FormType.EDIT;

  const handleCloseModal = () => {
    setIsSubmitting(false);
    reset();
    onClose();
    if (onCancel) {
      onCancel();
    }
    if (touchpointId && id) {
      // Fire-and-forget; releasing the lock should not block modal close.
      touchpointVersionApi
        .releaseLock({
          clientId: currentClient.id,
          campaignId: currentCampaign.id,
          touchpointId,
          id,
        })
        .catch((error) => {
          console.error("Failed to release touchpoint version lock", error);
        });
    }
  };

  const handleFormSubmit = async (data: FormData) => {
    if (onSubmit) {
      setIsSubmitting(true);
      try {
        const trimmedCopyCode = data.copyCode?.trim();
        if (canEditCopyCode && trimmedCopyCode && touchpointId) {
          await partialUpdateMutation.mutateAsync({
            id: touchpointId,
            data: { copyCode: trimmedCopyCode },
          });
        }

        await onSubmit({
          id,
          description: data.description,
          applyClientTheme: isEditFormType ? applyClientTheme : isClientThemeApplied,
          fiscalYear: data.fiscalYear,
          copyCode: trimmedCopyCode || undefined,
        });
        handleCloseModal();
      } catch (error) {
        toast.error({
          data: {
            title: "Failed to save creative. Please try again.",
            message: getErrorMessage(error?.response?.data),
          },
        });
      } finally {
        setIsSubmitting(false);
      }
    }
    reset();
  };

  if (!isOpen) return null;

  // Undefined once the form is valid, which is what keeps `Tooltip` out of the tree entirely.
  const saveBlockedMessage = isValid
    ? undefined
    : isTemplate
      ? "A description is required"
      : "A description and fiscal year are required";

  return (
    <FormDialog
      open
      onClose={handleCloseModal}
      headline={isEditFormType ? "Edit version details" : "Create draft"}
      description={
        <Typography component="span" sx={VERSION_EYEBROW_SX}>
          {parentName && `${parentName} | `}
          {name || ""}
        </Typography>
      }
      form={methods}
      onSubmit={handleFormSubmit}
      submitDisabled={!isValid}
      submitting={isSubmitting}
      submitTooltip={saveBlockedMessage}
      submitButtonText={isEditFormType ? "Update details" : saveText || "Create draft"}
      submitLoadingMessage={isEditFormType ? "Updating..." : "Creating..."}>
      {helperText && <span>{helperText}</span>}

      {!isTemplate && (
        <Box sx={FISCAL_YEAR_ROW_SX}>
          <Controller
            name="fiscalYear"
            control={control}
            rules={{ required: "Fiscal year is required" }}
            render={({ field, fieldState }) => (
              <Select
                label="Fiscal year"
                selectName={`fiscal-year-select-${field.name}`}
                ariaLabel="Select fiscal year"
                required
                options={fyOptions}
                value={fyOptions.find((opt) => opt.value === field.value)}
                onChange={(selected) => {
                  field.onChange(selected ? Number(selected.value) : null);
                }}
                errMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="copyCode"
            control={control}
            rules={{
              maxLength: {
                value: COPY_CODE_MAX_LENGTH,
                message: "Please do not exceed character limit.",
              },
            }}
            render={({ field, fieldState }) =>
              canEditCopyCode ? (
                <TextFieldWithCounter
                  label="Copy code"
                  errMessage={fieldState.error?.message}
                  {...field}
                />
              ) : (
                <TextField
                  label="Copy code"
                  disabled
                  errMessage={fieldState.error?.message}
                  {...field}
                  value={field.value || ""}
                  actionRowSx={{ gap: 0 }}
                  actionTrigger={
                    <IconButton
                      variant="text"
                      aria-label="Copy to clipboard"
                      onClick={() => {
                        navigator.clipboard.writeText(field.value ?? "");
                        toast.success({ data: { title: "Copied to clipboard" } });
                      }}>
                      <Icons.ContentCopy />
                    </IconButton>
                  }
                />
              )
            }
          />
        </Box>
      )}

      <Controller
        name="description"
        control={control}
        rules={{
          required: "Description is required. Please write a short description for this version.",
        }}
        render={({ field, fieldState: { error } }) => (
          <TextArea
            {...field}
            data-testid="version-modal-input-description"
            required
            label="Description"
            rows={2}
            errMessage={error?.message}
            autoFocus
          />
        )}
      />

      {isEmailType && !isEditFormType && (
        <div className="flex flex-col">
          <Switch
            toggleName="Enable client theme"
            checked={isClientThemeApplied}
            onChange={setIsClientThemeApplied}
            sx={{ mt: 1, mb: 0.5 }}
          />
        </div>
      )}
    </FormDialog>
  );
};

export default VersionEditModal;
